import React from 'react';
import { motion } from 'framer-motion';
import { Quote, Star, Crown, Hotel, Utensils, Sparkles, Users, Globe } from 'lucide-react';

/**
 * Premium reusable sections for landing/home pages.
 * Drop these into any page to instantly add testimonials, gallery, stats.
 */

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

const TESTIMONIALS = [
  {
    name: 'Priya & Rahul Sharma',
    role: 'Family Members · 3 years',
    quote: 'BCI feels like having a personal concierge for our family. From weekend pool days for the kids to anniversary dinners — it has truly elevated how we live.',
    accent: '#D4AF37',
  },
  {
    name: 'Karthik Reddy',
    role: 'Executive Member',
    quote: 'The corporate plan has been a brilliant addition for my team. Premium hotel rates, executive lounges and a relationship manager I can actually call.',
    accent: '#3B82F6',
  },
  {
    name: 'Anitha Iyer',
    role: 'Platinum Member',
    quote: 'Beautifully curated. The concierge actually knows our preferences. We booked a spa weekend in Goa with one WhatsApp message.',
    accent: '#22C55E',
  },
];

const GALLERY = [
  { icon: Hotel, title: 'Luxury Hotels', desc: 'Premium privileges across 600+ partner hotels & resorts', accent: '#D4AF37' },
  { icon: Utensils, title: 'Fine Dining', desc: 'Member-only access to chef-curated menus & priority bookings', accent: '#3B82F6' },
  { icon: Sparkles, title: 'Wellness & Spa', desc: 'Spa, gym & retreat experiences at signature destinations', accent: '#22C55E' },
  { icon: Users, title: 'Family Activities', desc: 'Pool clubs, kids\u2019 zones & weekend programs', accent: '#FACC15' },
  { icon: Crown, title: 'Premium Events', desc: 'Founder\u2019s evenings, festivals & signature experiences', accent: '#EF4444' },
  { icon: Globe, title: 'Worldwide Reach', desc: 'From India to global partner cities — one card opens all', accent: '#A78BFA' },
];

export const TestimonialsSection = () => (
  <section className="relative py-20 overflow-hidden">
    <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ background: 'radial-gradient(ellipse at top, rgba(212,175,55,0.15), transparent 55%)' }} />
    <div className="max-w-7xl mx-auto px-4 sm:px-8 relative">
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="text-center mb-12">
        <p className="text-[10px] sm:text-xs tracking-[0.4em] uppercase text-[#D4AF37] mb-3">— Member Stories —</p>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 tracking-wide" style={{ fontFamily: 'Cinzel, serif' }}>
          Trusted by <span className="text-[#D4AF37]">Discerning Families</span>
        </h2>
        <p className="text-gray-400 max-w-2xl mx-auto">From first-time members to long-time loyalists — here\u2019s what our community shares.</p>
      </motion.div>

      <div className="grid md:grid-cols-3 gap-5">
        {TESTIMONIALS.map((t, i) => (
          <motion.div
            key={t.name}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            custom={i}
            variants={fadeUp}
            className="bg-gradient-to-br from-[#0F0F10] to-[#1A1A1C] border border-white/5 hover:border-[#D4AF37]/30 rounded-2xl p-6 transition-all duration-500 hover:translate-y-[-4px] hover:shadow-[0_20px_50px_-10px_rgba(212,175,55,0.18)]"
            data-testid={`testimonial-${i}`}
          >
            <Quote className="w-8 h-8 mb-4" style={{ color: t.accent }} />
            <p className="text-gray-200 leading-relaxed mb-5 italic">"{t.quote}"</p>
            <div className="flex items-center gap-3 pt-4 border-t border-white/5">
              <div className="w-11 h-11 rounded-full flex items-center justify-center font-bold text-lg" style={{ background: `${t.accent}22`, color: t.accent, border: `1px solid ${t.accent}44`, fontFamily: 'Cinzel, serif' }}>
                {t.name.charAt(0)}
              </div>
              <div>
                <p className="text-white font-semibold text-sm">{t.name}</p>
                <p className="text-xs text-gray-400">{t.role}</p>
                <div className="flex gap-0.5 mt-1">{[...Array(5)].map((_, k) => <Star key={k} className="w-3 h-3 fill-[#D4AF37] text-[#D4AF37]" />)}</div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export const GallerySection = () => (
  <section className="relative py-20 bg-[#0A0A0B] overflow-hidden">
    <div className="absolute inset-0 opacity-30 pointer-events-none" style={{ background: 'radial-gradient(ellipse at bottom, rgba(13,27,63,0.6), transparent 60%)' }} />
    <div className="max-w-7xl mx-auto px-4 sm:px-8 relative">
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="text-center mb-12">
        <p className="text-[10px] sm:text-xs tracking-[0.4em] uppercase text-[#D4AF37] mb-3">— Experiences —</p>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 tracking-wide" style={{ fontFamily: 'Cinzel, serif' }}>
          A World of <span className="text-[#D4AF37]">Privileges</span>
        </h2>
        <p className="text-gray-400 max-w-2xl mx-auto">Every BCI card unlocks curated experiences across hospitality, dining, wellness and family lifestyle.</p>
      </motion.div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {GALLERY.map((g, i) => {
          const Ico = g.icon;
          return (
            <motion.div
              key={g.title}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              custom={i}
              variants={fadeUp}
              className="relative bg-[#1A1A1C] border border-white/5 hover:border-[#D4AF37]/30 rounded-2xl p-7 overflow-hidden group transition-all duration-500"
              data-testid={`gallery-${g.title.toLowerCase().replace(/\s/g, '-')}`}
            >
              <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full opacity-15 blur-3xl group-hover:opacity-30 transition-opacity duration-700" style={{ background: g.accent }} />
              <div className="relative">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-5" style={{ background: `${g.accent}15`, border: `1px solid ${g.accent}40` }}>
                  <Ico className="w-7 h-7" style={{ color: g.accent }} />
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Cinzel, serif' }}>{g.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{g.desc}</p>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

export const StatsBand = () => (
  <section className="py-14 border-y border-[#D4AF37]/15 bg-gradient-to-r from-[#0A0A0B] via-[#0D1B3F]/30 to-[#0A0A0B]">
    <div className="max-w-6xl mx-auto px-4 sm:px-8 grid grid-cols-2 sm:grid-cols-4 gap-6">
      {[
        { v: '600+', l: 'Partner Venues' },
        { v: '877+', l: 'Active Members' },
        { v: '5', l: 'Premium Tiers' },
        { v: '24/7', l: 'Concierge' },
      ].map((s, i) => (
        <motion.div key={s.l} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp} className="text-center">
          <p className="text-3xl sm:text-5xl font-bold text-[#D4AF37] mb-1" style={{ fontFamily: 'Cinzel, serif' }}>{s.v}</p>
          <p className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-gray-400">{s.l}</p>
        </motion.div>
      ))}
    </div>
  </section>
);
